'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { RotateCcw, RotateCw, X } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { useOpenFailed, useWorkspaceActions } from '@/components/workspace/store';
import { useToastStackTarget } from '@/components/workspace/toast-stack';
import {
  UNNAMED_ITEM_LABEL,
  resolveItemLabel,
  useLiveContentMeta,
} from '@/components/workspace/item-label';
import type { WorkspaceItemDTO } from '@/lib/workspaces/types';
import { cn } from '@/lib/utils';

/**
 * The canvas's two offers about a window, mounted ONCE in the canvas and
 * drawn in one place: "Closed <name>" with Undo, for about eight seconds,
 * after a window's Close dot is used; and "Couldn't open <name>." with Try
 * again, when a window someone opened had to be withdrawn (below).
 *
 * Why (the Revent e2e run of 2026-09-26/27). Focus escaped an app's frame
 * onto its window's Close dot, a keypress closed the window, and there was
 * neither a confirm nor a way back. A confirm would be the wrong tool: a
 * close keeps the app's content and its collab room (both keyed by refId,
 * see `store.removeItem`), so it is fully reversible, and Undo is the house
 * pattern for a reversible act. The window comes back as it was: same id,
 * same state, so its project folder, version pin, size and position are all
 * the ones it had.
 *
 * The seam is a `window` event, `ws:window-closed`, that node-frame.tsx
 * dispatches with the whole item BEFORE it calls `removeItem`, so this
 * component never has to reach into a node. A pinned window's Close dot
 * docks instead of closing and dispatches nothing, so no toast. A second
 * close replaces the first: one window is offered at a time, the latest.
 *
 * "COULDN'T OPEN <name>." (fix round 2, 2026-09-30). `addItem` paints a new
 * window at once, then asks `resolve-ref` about it; after three 15 s
 * timeouts or a 5xx it withdraws the window, and it used to do so without a
 * word, so an open simply did not happen. The store now leaves one offer
 * (`OpenFailedOffer`, `useOpenFailed`), drawn here beside the Undo offer:
 * "Couldn't open Revent." and Try again, which runs the same open again. The
 * withdrawal itself is unchanged: the window never reached the shared doc.
 *
 * WHERE THEY SIT (fix round 2, 2026-09-30). On a desktop the Undo offer drew
 * in the shared prompt column, at the canvas's top right, and there it lay
 * over the next window's title-bar buttons: close the top window of a
 * cascade, then click Close on the window under it, and the click landed on
 * Undo and reopened the window just closed. Every window control on a
 * desktop is in a title bar, at a window's TOP, and new windows open at the
 * top of the screen you are on (the Stage algorithm, rules 3 to 5), so the
 * offers now sit at the BOTTOM CENTRE of the canvas, above its bottom edge
 * and clear of the rail, the way macOS puts its transient notices low in the
 * middle (`toastDockPlacement`). A handset keeps lane C's pill, docked just
 * above the bottom navigation. "Desktop" is where the prompt column exists
 * (md and up, once mounted), the same test this component always used; the
 * offers are no longer drawn INTO that column.
 */

/** Dispatched by the Close dot with the full item, before the remove. */
export const WINDOW_CLOSED_EVENT = 'ws:window-closed';
/** How long Undo is offered. Long enough to notice, short enough to fade. */
export const CLOSED_WINDOW_UNDO_MS = 8000;
/** How long "Couldn't open" is offered: a little longer, it asks for a decision. */
export const OPEN_FAILED_OFFER_MS = 10_000;

/**
 * Where the window offers sit: at the bottom centre of the canvas on a
 * desktop, never over a title bar; above the bottom navigation on a handset.
 */
export function toastDockPlacement(desktop: boolean): 'bottom-centre' | 'above-navigation' {
  return desktop ? 'bottom-centre' : 'above-navigation';
}

export function announceWindowClosed(item: WorkspaceItemDTO): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(WINDOW_CLOSED_EVENT, { detail: { item } }));
}

/** The event's detail, held to its shape: an object item with a string id. */
function itemOf(detail: unknown): WorkspaceItemDTO | null {
  const item = (detail as { item?: unknown } | null)?.item;
  if (!item || typeof item !== 'object') return null;
  const it = item as WorkspaceItemDTO;
  if (typeof it.id !== 'string' || typeof it.workspaceId !== 'string') return null;
  if (!it.state || typeof it.state !== 'object') return null;
  return it;
}

const CARD_CLASS =
  'flex items-center gap-2 rounded-xl border border-border/60 bg-popover px-3 py-2 text-sm text-foreground shadow-lg animate-in slide-in-from-bottom-2 fade-in duration-200';
const ACTION_CLASS =
  'inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-xs font-medium transition-colors hover:bg-accent';
const DISMISS_CLASS =
  'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground';

export function ClosedWindowUndo() {
  const t = useT();
  const { restoreItem, bringToFront, retryFailedOpen, dismissFailedOpen } = useWorkspaceActions();
  const live = useLiveContentMeta();
  // The prompt column exists: a desktop (md and up, once mounted). The
  // offers are not drawn into it (see WHERE THEY SIT); it only answers
  // "desktop or handset" the way it always has for this component.
  const desktop = useToastStackTarget('default') !== null;
  const failed = useOpenFailed();
  const [closed, setClosed] = useState<{ item: WorkspaceItemDTO; at: number } | null>(null);

  useEffect(() => {
    const onClosed = (e: Event) => {
      const item = itemOf((e as CustomEvent).detail);
      if (!item) return;
      setClosed({ item, at: Date.now() });
    };
    window.addEventListener(WINDOW_CLOSED_EVENT, onClosed);
    return () => window.removeEventListener(WINDOW_CLOSED_EVENT, onClosed);
  }, []);

  // One timer per offer: a later close replaces the offer and restarts it.
  useEffect(() => {
    if (!closed) return;
    const timer = setTimeout(() => setClosed((cur) => (cur === closed ? null : cur)), CLOSED_WINDOW_UNDO_MS);
    return () => clearTimeout(timer);
  }, [closed]);

  // The same for "Couldn't open": a later failure is a new offer (`seq`).
  useEffect(() => {
    if (!failed) return;
    const timer = setTimeout(() => dismissFailedOpen(failed.seq), OPEN_FAILED_OFFER_MS);
    return () => clearTimeout(timer);
  }, [failed, dismissFailedOpen]);

  if (!closed && !failed) return null;
  const cards: ReactNode[] = [];
  // ON A HANDSET THE TWO CONTROLS ARE A THUMB'S TARGETS (the visual pass of
  // 2026-10-03, on the phone's Stage list, where a close asks nothing and
  // this card is the only way back): Undo measured 67 x 26 and the dismiss
  // 24 x 24, 8 px apart. Both are 44 px tall there now; a desktop keeps its
  // pointer sizes. `cn` merges, so the later size wins.
  const cardClass = desktop ? CARD_CLASS : cn(CARD_CLASS, 'py-1 pr-1');
  const actionClass = desktop ? ACTION_CLASS : cn(ACTION_CLASS, 'min-h-11 px-3 text-sm');
  const dismissClass = desktop ? DISMISS_CLASS : cn(DISMISS_CLASS, 'h-11 w-11');

  if (closed) {
    const { item } = closed;
    const name = resolveItemLabel(item, live, UNNAMED_ITEM_LABEL);
    const undo = () => {
      restoreItem(item);
      bringToFront(item.id);
      window.dispatchEvent(new CustomEvent('ws:focus-item', { detail: { itemId: item.id } }));
      setClosed(null);
    };
    cards.push(
      <div key="closed" role="status" data-testid="closed-window-undo" className={cardClass}>
        <span className="min-w-0 flex-1 truncate">
          {t('workspace.canvas.node.closedWindow', { name })}
        </span>
        <button type="button" onClick={undo} data-testid="closed-window-undo-button" className={actionClass}>
          <RotateCcw className="h-3.5 w-3.5" />
          {t('workspace.canvas.node.undoClose')}
        </button>
        <button type="button" onClick={() => setClosed(null)} aria-label={t('actions.dismiss')} className={dismissClass}>
          <X className="h-3.5 w-3.5" />
        </button>
      </div>,
    );
  }

  if (failed) {
    // The window as it was painted: its live name, else the name the opener
    // gave it; "the app" when it had none.
    const name = resolveItemLabel(failed.item, live, '');
    cards.push(
      <div key="open-failed" role="alert" data-testid="open-failed" className={cardClass}>
        <span className="min-w-0 flex-1 truncate">
          {name
            ? t('workspace.canvas.node.openFailed', { name })
            : t('workspace.canvas.node.openFailedUnnamed')}
        </span>
        <button
          type="button"
          onClick={() => retryFailedOpen(failed.seq)}
          data-testid="open-failed-retry"
          className={actionClass}
        >
          <RotateCw className="h-3.5 w-3.5" />
          {t('errors.tryAgain')}
        </button>
        <button
          type="button"
          onClick={() => dismissFailedOpen(failed.seq)}
          aria-label={t('actions.dismiss')}
          className={dismissClass}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>,
    );
  }

  if (toastDockPlacement(desktop) === 'bottom-centre') {
    // THE DESKTOP: the bottom centre of the canvas (this component is mounted
    // inside the canvas wrapper, so `absolute` is the canvas's box: right of
    // the rail, the whole width in full screen), 16 px above its bottom edge
    // and the home indicator's inset. Above the windows and Stage's chrome,
    // below the dialogs (z-[220] and up). Only the cards take the pointer.
    return (
      <div
        data-window-toasts="bottom-centre"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[170] flex justify-center px-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
      >
        <div className="pointer-events-auto flex w-full max-w-[384px] flex-col gap-2">{cards}</div>
      </div>
    );
  }
  // The handset pill docks just ABOVE the bottom navigation (its measured
  // height, `--ws-bar-px`, published by mobile-tab-bar.tsx), or above the
  // gesture inset when the navigation is hidden (in-tab full screen), for
  // its few seconds: the one platform surface allowed over a presented app,
  // and never one that stays (handoff 2026-09-29, section 4.6).
  return (
    <div
      data-window-toasts="above-navigation"
      className="pointer-events-auto fixed inset-x-3 z-[170] flex flex-col gap-2"
      style={{ bottom: 'calc(max(var(--ws-bar-px, 0px), env(safe-area-inset-bottom, 0px)) + 0.75rem)' }}
    >
      {cards}
    </div>
  );
}
